import { useQuery } from '@apollo/client';
import CustomPasswordConfig from '@nile/banyan/src/Pebbles/customPasswordConfig';
import { COMMON_CONSTANTS, getTranslatedText } from '@nile/components';
import { AccountClient, GET_DEFAULT_PASSWORD_POLICIES } from '@nile/graphql';
import contentWrapperConfig from '@nile/home/src/Constants/contentWrapper';
import {
  Button,
  ButtonGroup,
  Heading,
  TextStyle
} from '@superops/ui-design-system';
import * as _ from 'lodash';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ContentWrapper } from 'sopslibrary';

function PasswordPolicy(props) {
  const { intl, securityConfig, type, title } = props;
  const [policies] = useState([
    {
      name: 'HIGH',
      label: `${getTranslatedText(intl, 'admin.passwordPolicy.type.high')} 😎`
    },
    {
      name: 'MEDIUM',
      label: `${getTranslatedText(intl, 'admin.passwordPolicy.type.medium')} 😐`
    },
    {
      name: 'LOW',
      label: `${getTranslatedText(intl, 'admin.passwordPolicy.type.low')} 😞`
    },
    {
      name:
        type === 'technician' ? 'CUSTOM_COMPANY_USER' : 'CUSTOM_CLIENT_USER',
      label: `${getTranslatedText(intl, 'admin.passwordPolicy.type.custom')} 🛠`
    }
  ]);
  const types = {
    technician: {
      label: 'Technician',
      field: 'companyUserPasswordPolicy'
    },
    requester: {
      label: 'Requester',
      field: 'clientUserPasswordPolicy'
    }
  };

  const policyType = policies.find((el) => {
    let name = securityConfig[types[type].field];
    if (securityConfig[types[type].field] === 'CUSTOM') {
      name =
        type === 'technician' ? 'CUSTOM_COMPANY_USER' : 'CUSTOM_CLIENT_USER';
    }
    return el.name === name;
  });

  const [selectedType, setSelectedType] = useState(policyType);

  const updateSelection = useCallback((el) => {
    setSelectedType(policies.find((policy) => policy.name === el.name));
    props.onChange(types[type].field, el.name);
  });

  return (
    <>
      <Heading>{title}</Heading>
      <TextStyle weight="Medium">
        <ButtonGroup segmented>
          {policies.map((el, idx) => {
            return (
              <>
                <Button
                  outline={!(selectedType?.name === el.name)}
                  onClick={updateSelection.bind(this, el)}
                  primary={selectedType?.name === el.name}
                >
                  {el.label}
                </Button>
              </>
            );
          })}
        </ButtonGroup>
      </TextStyle>
      <TextStyle weight="Medium">
        <PasswordPolicyConditions
          intl={intl}
          selectedPolicy={selectedType}
          config={securityConfig}
          type={type}
          handleCustomPasswordConfig={props.handleCustomPasswordConfig}
        />
      </TextStyle>
    </>
  );
}

function PasswordPolicyConditions(props) {
  const [policy, setPolicy] = useState(null);

  const { selectedPolicy, config, type } = props;

  const { gapWithinNestedSection, defaultClasses } = contentWrapperConfig;

  const { loading, data: defaultPolicies, error } = useQuery(
    GET_DEFAULT_PASSWORD_POLICIES,
    {
      client: AccountClient,
      onCompleted({ getDefaultPasswordPoliciesConfig }) {
        setPolicy(
          getDefaultPasswordPoliciesConfig.find(
            (el) => el.passwordPolicyType === selectedPolicy?.name
          )
        );
      }
    }
  );
  useEffect(() => {
    if (defaultPolicies) {
      const typeKey =
        type === 'technician'
          ? 'companyUserPasswordPolicy'
          : 'clientUserPasswordPolicy';
      const configKey =
        type === 'technician'
          ? 'companyUserCustomPasswordPolicy'
          : 'clientUserCustomPasswordPolicy';
      if (_.includes(config[typeKey], 'CUSTOM') && config[configKey] !== null) {
        const updatedConfig = _.cloneDeep(config[configKey]);
        updatedConfig['passwordPolicyType'] = selectedPolicy.name;
        setPolicy(updatedConfig);
      } else {
        setPolicy(
          defaultPolicies.getDefaultPasswordPoliciesConfig.find(
            (el) => el.passwordPolicyType === selectedPolicy?.name
          )
        );
      }
    }
  }, [selectedPolicy]);

  if (loading || !policy) {
    return <div>loading..</div>;
  }
  if (error) {
    return <div>error...</div>;
  }
  const descriptionMap = {
    length: {
      label: policy.length ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.passwordLength" />{' '}
          <TextStyle weight="Bold">
            {policy.length}{' '}
            <FormattedMessage id="admin.requesterLogin.passwordLengthChar" />{' '}
          </TextStyle>
        </TextStyle>
      ) : (
        ''
      )
    },
    isAlphaNumeric: {
      label: policy.isAlphaNumeric ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.alphaNUmeric" />
        </TextStyle>
      ) : (
        ''
      )
    },
    isMixCaseWithSpecialChar: {
      label: policy.isMixCaseWithSpecialChar ? (
        <FormattedMessage id="admin.requesterLogin.mixCase" />
      ) : (
        ''
      )
    },
    checkEmailMatch: {
      label: policy.checkEmailMatch ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.emailShouldNotMatch" />
        </TextStyle>
      ) : (
        ''
      )
    },
    checkPrevPasswordMatchCount: {
      label: policy.checkPrevPasswordMatchCount ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.shouldNotMatchPrevious" />{' '}
          <TextStyle weight="Bold">
            {policy.checkPrevPasswordMatchCount}{' '}
            <FormattedMessage id="admin.requesterLogin.passwords" />
          </TextStyle>
        </TextStyle>
      ) : (
        ''
      )
    },
    allowedFailureAtempt: {
      label: policy.allowedFailureAtempt ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.lockUserAccount" />{' '}
          <TextStyle weight="Bold">
            {policy.allowedFailureAtempt}{' '}
            <FormattedMessage id="admin.requesterLogin.unsuccessfulAttempts" />
          </TextStyle>
        </TextStyle>
      ) : (
        ''
      )
    },
    passwordExpiryDays: {
      label: policy.passwordExpiryDays ? (
        <TextStyle>
          <FormattedMessage id="admin.requesterLogin.passwordExpiry" />{' '}
          <TextStyle weight="Bold">
            {policy.passwordExpiryDays}{' '}
            <FormattedMessage id="admin.requesterLogin.passwordExpiryDays" />
          </TextStyle>
        </TextStyle>
      ) : (
        ''
      )
    }
  };

  const handleChange = (key, value) => {
    const updatedPolicy = _.cloneDeep(policy);
    updatedPolicy[key] = value;
    setPolicy(updatedPolicy);
    const processedPolicy = _.cloneDeep(updatedPolicy);
    if (_.has(processedPolicy, 'passwordPolicyType')) {
      delete processedPolicy.passwordPolicyType;
    }
    props.handleCustomPasswordConfig(processedPolicy);
  };

  return (
    <div className="font-wt-normal">
      {policy.passwordPolicyType === 'CUSTOM_COMPANY_USER' ||
      policy.passwordPolicyType === 'CUSTOM_CLIENT_USER' ? (
        <CustomPasswordConfig config={policy} handleChange={handleChange} />
      ) : (
        <ContentWrapper
          className={`${defaultClasses} white-transparent-bg`}
          gap={gapWithinNestedSection}
          nopadding
        >
          {Object.keys(descriptionMap).map((el) => {
            return descriptionMap[el].label;
          })}
        </ContentWrapper>
      )}
    </div>
  );
}

const SCHEMA_MAP = {
  configurationId: 'ID',
  ssoType: 'String',
  idpDetail: 'IdpSamlProviderDetailInput',
  spDetail: 'SpSamlProviderDetail',
  sessionInactiveDuration: 'Int',
  sessionInactiveUnit: 'String',
  sessionTrustEnable: 'Boolean',
  ipWhitelistEnable: 'Boolean',
  ipWhitelistRange: 'String',
  companyUserPasswordPolicy: 'String',
  clientUserPasswordPolicy: 'String',
  companyUserCustomPasswordPolicy: 'PasswordPolicyInput',
  clientUserCustomPasswordPolicy: 'PasswordPolicyInput',
  enforceTfa: 'Boolean'
};

const getDifference = (object, base) => {
  let difference = {};
  for (var prop in object) {
    if (!isEqual(object[prop], base[prop])) {
      difference[prop] = object[prop];
    }
  }
  return difference;
};

const getVariables = (diff) => {
  let variables = {};
  for (var prop in diff) {
    variables[prop] = {
      value: diff[prop],
      type: SCHEMA_MAP[prop],
      inputVariable: prop !== 'configurationId',
      required: prop === 'configurationId'
    };
  }
  return variables;
};

const getFields = () => {
  return [
    'ssoType',
    {
      idpDetail: ['certificate', 'idpLoginUrl', 'idpLogoutUrl']
    },
    {
      spDetail: ['spConsumerUrl']
    },
    'sessionInactiveDuration',
    'sessionInactiveUnit',
    'sessionTrustEnable',
    'ipWhitelistEnable',
    'ipWhitelistRange',
    'companyUserPasswordPolicy',
    'clientUserPasswordPolicy',
    {
      companyUserCustomPasswordPolicy: [
        'length',
        'isAlphaNumeric',
        'isMixCaseWithSpecialChar',
        'checkEmailMatch',
        'checkPrevPasswordMatchCount',
        'allowedFailureAtempt',
        'passwordExpiryDays'
      ]
    },
    {
      clientUserCustomPasswordPolicy: [
        'length',
        'isAlphaNumeric',
        'isMixCaseWithSpecialChar',
        'checkEmailMatch',
        'checkPrevPasswordMatchCount',
        'allowedFailureAtempt',
        'passwordExpiryDays'
      ]
    },
    'enforceTfa'
  ];
};

const Pass = injectIntl(PasswordPolicy);

export { Pass as PasswordPolicy, getFields, getVariables, getDifference };
